import React, { useState, useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { regions, formatPrice, buildPricingUrl } from '../../config/pricingConfig';

const RESOURCE_TYPES = [
  { value: 'compute', label: 'Compute (VM)' },
  { value: 'storage', label: 'Storage' },
  { value: 'manageddisk', label: 'Managed Disk' },
  { value: 'publicip', label: 'Public IP' },
];

const buildFilter = (resourceType, sku, region) => {
  switch (resourceType) {
    case 'compute':
      return `armRegionName eq '${region}' and armSkuName eq '${sku}' and priceType eq 'Consumption' and serviceFamily eq 'Compute' and contains(productName, 'Cloud Services') ne true and contains(skuName, 'Spot') ne true and contains(skuName, 'Low Priority') ne true`;
    case 'storage':
      return `armRegionName eq '${region}' and serviceFamily eq 'Storage' and serviceName eq 'Storage' and skuName eq '${sku}'`;
    case 'manageddisk':
      return `armRegionName eq '${region}' and serviceName eq 'Storage' and type eq 'Consumption' and contains(skuName, '${sku}') and contains(meterName, 'Free') ne true and contains(meterName, 'Mount') ne true`;
    case 'publicip':
      return `armRegionName eq '${region}' and productName eq 'IP Addresses' and serviceName eq 'Virtual Network' and type eq 'Consumption' and contains(meterName, 'Static') eq true`;
    default:
      return null;
  }
};

const PriceComparison = () => {
  const [resourceType, setResourceType] = useState('compute');
  const [sku, setSku] = useState('');
  const [selectedRegions, setSelectedRegions] = useState(['eastus', 'westus', 'northeurope']);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleRegionChange = (idx, value) => {
    setSelectedRegions((prev) => {
      const updated = [...prev];
      updated[idx] = value;
      return updated;
    });
  };

  const handleCompare = async () => {
    if (!sku) { setError('Please enter a SKU to compare.'); return; }
    setLoading(true);
    setError(null);
    setResults({});

    const fetches = selectedRegions.map(async (region) => {
      if (!region) return [region, null];
      const filter = buildFilter(resourceType, sku, region);
      const url = buildPricingUrl(`$filter=${filter}`);
      try {
        const res = await fetch(url);
        const data = await res.json();
        return [region, data.Items ?? []];
      } catch {
        return [region, []];
      }
    });

    const settled = await Promise.all(fetches);
    const map = Object.fromEntries(settled);
    setResults(map);
    setLoading(false);
  };

  // Collect all unique SKU names across regions for row alignment
  const allSkuNames = useMemo(
    () => [
      ...new Set(
        Object.values(results)
          .flat()
          .filter(Boolean)
          .map((item) => item.skuName)
      ),
    ],
    [results]
  );

  const getPrice = (region, skuName) => {
    const items = results[region] || [];
    return items.find((i) => i.skuName === skuName);
  };

  const cheapestRegion = (skuName) => {
    let best = null;
    let bestPrice = Infinity;
    selectedRegions.forEach((region) => {
      const item = getPrice(region, skuName);
      if (item && item.unitPrice < bestPrice) {
        bestPrice = item.unitPrice;
        best = region;
      }
    });
    return best;
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2">Price Comparison</h2>
      <p className="text-sm text-gray-500 mb-6">
        Compare prices for the same SKU across up to 3 Azure regions side-by-side.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Select value={resourceType} onValueChange={setResourceType}>
          <SelectTrigger>
            <SelectValue placeholder="Resource type" />
          </SelectTrigger>
          <SelectContent>
            {RESOURCE_TYPES.map((r) => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          placeholder="SKU (e.g. Standard_D2s_v3)"
        />
        {selectedRegions.map((reg, idx) => (
          <Select key={idx} value={reg} onValueChange={(v) => handleRegionChange(idx, v)}>
            <SelectTrigger>
              <SelectValue placeholder={`Region ${idx + 1}`} />
            </SelectTrigger>
            <SelectContent>
              {regions.map((r) => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}
        <Button onClick={handleCompare} disabled={loading || !sku}>
          {loading ? 'Comparing…' : 'Compare'}
        </Button>
      </div>

      {error && <div className="text-red-500 mb-4 text-sm">{error}</div>}

      {allSkuNames.length > 0 && (
        <div className="overflow-auto rounded-lg border shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="p-3 text-left">SKU / Product</th>
                {selectedRegions.map((region) => {
                  const label = regions.find((r) => r.value === region)?.label ?? region;
                  return (
                    <th key={region} className="p-3 text-right">{label}</th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {allSkuNames.map((skuName) => {
                const cheapest = cheapestRegion(skuName);
                return (
                  <tr key={skuName} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium">{skuName}</td>
                    {selectedRegions.map((region) => {
                      const item = getPrice(region, skuName);
                      const isCheapest = region === cheapest;
                      return (
                        <td
                          key={region}
                          className={`p-3 text-right ${isCheapest ? 'text-green-600 font-semibold' : ''}`}
                        >
                          {item ? (
                            <>
                              {formatPrice(item.unitPrice, item.currencyCode, item.unitOfMeasure)}
                              {isCheapest && (
                                <span className="ml-1 text-xs bg-green-100 text-green-700 px-1 rounded">
                                  best
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && allSkuNames.length === 0 && Object.keys(results).length > 0 && (
        <p className="text-gray-500 text-sm">No results found. Try a different SKU or resource type.</p>
      )}
    </div>
  );
};

export default PriceComparison;
